import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, RotateCcw, Target, Award, Mail, Shield, Bell, Trash2, ChevronRight, Sparkles, Stethoscope, ChevronDown, CreditCard } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [notifyTime, setNotifyTime] = useState(user?.notify_time || "08:00");
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [notifyEnabled, setNotifyEnabled] = useState(user?.notify_enabled || false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openLegal, setOpenLegal] = useState("");

  const toggleNotify = async () => {
    const next = !notifyEnabled;
    setNotifyEnabled(next);
    try {
      await api.put("/profile", { notify_enabled: next, notify_time: notifyTime });
      await refresh();
      toast.success(next ? "Daily reminders on" : "Reminders turned off");
    } catch (e) {
      setNotifyEnabled(!next);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete("/account");
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { first_name: firstName, notify_time: notifyTime });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    try {
      await api.post("/profile/reset-progress");
      await refresh();
      toast.success("Progress reset, back to Day 1");
      setConfirmReset(false);
      navigate("/home");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const [portalBusy, setPortalBusy] = useState(false);
  const manageSubscription = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open the billing portal");
      setPortalBusy(false);
    }
  };

  const subLabel = { active: "Active", trialing: "Trial", past_due: "Payment due", canceled: "Cancelled" }[user?.subscription_status] || (user?.has_access ? "Active" : "Inactive");
  const renewDate = user?.subscription_current_period_end
    ? new Date(user.subscription_current_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : null;
  const planLine = user?.subscription_interval === "month"
    ? "£4.99 a month"
    : user?.subscription_interval === "year"
      ? "£47.99 a year"
      : "Subscription";
  const planTitle = user?.subscription_interval === "month" ? "Monthly subscription" : "Annual subscription";

  return (
    <div className="px-5 pt-8 animate-fade-rise" data-testid="profile-page">
      <p className="overline-label">Your account</p>
      <h1 className="font-serif text-3xl font-bold text-ink mt-2">Profile</h1>

      {/* summary */}
      <div className="mt-5 bg-brand rounded-3xl p-6 text-white">
        <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center font-serif text-2xl font-bold">
          {(user?.first_name || "?")[0]?.toUpperCase()}
        </div>
        <div className="font-serif text-2xl font-bold mt-3">{user?.first_name}</div>
        <div className="flex items-center gap-1.5 text-white/60 text-sm mt-1">
          <Mail size={13} /> {user?.email}
        </div>
      </div>

      {/* focus + score */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl p-4 card-shadow">
          <Target size={16} className="text-brand" />
          <div className="text-ink-muted text-xs mt-2">Your focus</div>
          <div className="font-semibold text-ink text-sm mt-0.5">{user?.priorities?.[0] || "Overall wellbeing"}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 card-shadow">
          <Award size={16} className="text-brand" />
          <div className="text-ink-muted text-xs mt-2">Starting score</div>
          <div className="font-semibold text-ink text-sm mt-0.5">{user?.starting_score ?? "–"}/100</div>
        </div>
      </div>

      {/* editable */}
      <div className="mt-4 bg-white rounded-2xl p-5 card-shadow space-y-4">
        <div>
          <label className="text-sm font-semibold text-ink">First name</label>
          <input
            data-testid="profile-firstname"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)]"
          />
        </div>
        <div>
          <label className="text-sm font-semibold text-ink">Daily reminder time</label>
          <input
            data-testid="profile-notify"
            type="time"
            value={notifyTime}
            onChange={(e) => setNotifyTime(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)]"
          />
        </div>
        <button
          data-testid="profile-save"
          onClick={saveProfile}
          disabled={saving}
          className="w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
        >
          <Save size={16} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* menopause details */}
      <div className="mt-4 bg-white rounded-2xl p-5 card-shadow" data-testid="profile-details">
        <p className="overline-label mb-3 block">Your journey</p>
        <div className="space-y-2.5 text-sm">
          <div className="flex justify-between"><span className="text-ink-muted">Menopause stage</span><span className="text-ink font-medium text-right">{user?.menopause_stage || "—"}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Daily commitment</span><span className="text-ink font-medium">{user?.daily_minutes || "—"}</span></div>
          <div className="flex justify-between gap-4"><span className="text-ink-muted flex-shrink-0">Primary priorities</span><span className="text-ink font-medium text-right">{(user?.priorities || []).join(", ") || "—"}</span></div>
        </div>
      </div>

      {/* subscription / trial */}
      {user?.role !== "admin" && (
        user?.access_state === "trial" && !user?.has_subscription ? (
          <div className="mt-4 bg-white rounded-2xl p-5 card-shadow" data-testid="profile-subscription">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles size={17} className="text-brand" />
                <div>
                  <div className="font-semibold text-ink text-sm">Free trial</div>
                  <div className="text-ink-muted text-xs mt-0.5">
                    {user.trial_days_left} {user.trial_days_left === 1 ? "day" : "days"} left
                  </div>
                </div>
              </div>
              <span data-testid="subscription-status" className="text-xs font-semibold px-3 py-1 rounded-full bg-sage text-brand">Trial</span>
            </div>
            <button
              data-testid="trial-subscribe-btn"
              onClick={() => navigate("/checkout")}
              className="mt-4 w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-2.5 rounded-full flex items-center justify-center gap-2 text-sm transition-colors"
            >
              <CreditCard size={15} /> Subscribe to keep access
            </button>
          </div>
        ) : (
        <div className="mt-4 bg-white rounded-2xl p-5 card-shadow" data-testid="profile-subscription">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard size={17} className="text-brand" />
              <div>
                <div className="font-semibold text-ink text-sm">{planTitle}</div>
                <div className="text-ink-muted text-xs mt-0.5">{planLine}</div>
              </div>
            </div>
            <span
              data-testid="subscription-status"
              className={`text-xs font-semibold px-3 py-1 rounded-full ${user?.subscription_status === "past_due" ? "bg-amber-100 text-amber-700" : "bg-sage text-brand"}`}
            >
              {subLabel}
            </span>
          </div>
          {renewDate && (
            <div className="text-ink-soft text-xs mt-3">
              {user?.subscription_status === "canceled" ? "Access until" : "Renews on"} {renewDate}
            </div>
          )}
          {user?.has_subscription && (
            <button
              data-testid="manage-subscription-btn"
              onClick={manageSubscription}
              disabled={portalBusy}
              className="mt-4 w-full border-2 border-[var(--brand)] text-brand font-semibold py-2.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60 text-sm"
            >
              <CreditCard size={15} /> {portalBusy ? "Opening…" : "Manage or cancel subscription"}
            </button>
          )}
        </div>
        )
      )}

      {/* my plans */}
      <div className="mt-4 space-y-3">
        <button data-testid="profile-plan-link" onClick={() => navigate("/plan")} className="w-full bg-white rounded-2xl p-4 card-shadow flex items-center justify-between">
          <span className="flex items-center gap-3 text-ink font-semibold text-sm"><Sparkles size={17} className="text-brand" /> My Support Plan</span>
          <ChevronRight size={18} className="text-ink-muted" />
        </button>
        <button data-testid="profile-doctor-link" onClick={() => navigate("/doctor-planner")} className="w-full bg-white rounded-2xl p-4 card-shadow flex items-center justify-between">
          <span className="flex items-center gap-3 text-ink font-semibold text-sm"><Stethoscope size={17} className="text-brand" /> Doctor Conversation Planner</span>
          <ChevronRight size={18} className="text-ink-muted" />
        </button>
      </div>

      {/* notifications */}
      <div className="mt-4 bg-white rounded-2xl p-5 card-shadow" data-testid="profile-notifications">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell size={17} className="text-brand" />
            <div>
              <div className="font-semibold text-ink text-sm">Daily reminder</div>
              <div className="text-ink-muted text-xs">e.g. "Your Day 8 Reset is ready."</div>
            </div>
          </div>
          <button
            data-testid="notify-toggle"
            onClick={toggleNotify}
            className={`w-12 h-7 rounded-full transition-colors relative ${notifyEnabled ? "bg-brand" : "bg-[var(--surface-accent)]"}`}
          >
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${notifyEnabled ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </div>

      {/* legal / settings */}
      <div className="mt-4 space-y-2">
        {[
          { k: "disclaimer", label: "Medical disclaimer", body: "The 30 Day Menopause Reset is an educational and self management program. It is not medical advice and does not diagnose, treat or cure any condition, nor does it replace care from a qualified healthcare professional. It is provided by The Faybelle Collection, trading as Meno Flames and Finesse. Always consult your doctor about your symptoms, especially before changing medication, and seek urgent care for any severe, sudden or concerning symptoms." },
          { k: "privacy", label: "Privacy", body: "Your check ins, symptoms, reflections and plans are private to your account. Only you can see them. We never sell your data. Payment card details are handled securely by Stripe and are never stored in this app." },
          { k: "terms", label: "Terms of use", body: "The 30 Day Menopause Reset is provided by The Faybelle Collection, trading as Meno Flames and Finesse. By using this app you agree that it is provided for educational purposes as-is, without medical warranty, for personal, non-commercial use. New members get a 7-day free trial with no card needed. After that, access continues through a subscription of £47.99 a year or £4.99 a month, which renews automatically until you cancel. You can cancel anytime from this screen and keep access until the end of your paid period. Questions: hello@menoflamesandfinesse.com" },
          { k: "privacypolicy", label: "Privacy policy", body: "The Faybelle Collection, trading as Meno Flames and Finesse, is responsible for your data. We store the information you provide (account details, onboarding answers, check ins, symptoms, reflections and plans) only to run the program for you and to send you account and program emails. Payments are processed by Stripe and emails are sent by our email provider; we never sell your data. You can delete your account and associated data at any time from this screen. For any privacy request, including access to or deletion of your data, email hello@menoflamesandfinesse.com" },
        ].map((item) => (
          <div key={item.k} className="bg-white rounded-2xl card-shadow overflow-hidden">
            <button
              data-testid={`legal-${item.k}`}
              onClick={() => setOpenLegal(openLegal === item.k ? "" : item.k)}
              className="w-full flex items-center justify-between px-5 py-3.5 text-left"
            >
              <span className="text-ink font-semibold text-sm">{item.label}</span>
              <ChevronDown size={18} className={`text-ink-muted transition-transform ${openLegal === item.k ? "rotate-180" : ""}`} />
            </button>
            {openLegal === item.k && <div className="px-5 pb-4 text-ink-soft text-sm leading-relaxed">{item.body}</div>}
          </div>
        ))}
      </div>

      {/* admin switch */}
      {user?.role === "admin" && (
        <button
          data-testid="profile-admin-btn"
          onClick={() => navigate("/admin")}
          className="mt-4 w-full bg-white border-2 border-[var(--brand)] text-brand font-semibold py-3 rounded-full flex items-center justify-center gap-2"
        >
          <Shield size={16} /> Admin dashboard
        </button>
      )}

      {/* danger + logout */}
      <div className="mt-4 space-y-3">
        {!confirmReset ? (
          <button
            data-testid="reset-progress-btn"
            onClick={() => setConfirmReset(true)}
            className="w-full bg-white text-ink-soft font-semibold py-3 rounded-full flex items-center justify-center gap-2 card-shadow"
          >
            <RotateCcw size={16} /> Reset my progress
          </button>
        ) : (
          <div className="bg-white rounded-2xl p-4 card-shadow">
            <p className="text-sm text-ink-soft">This clears your completed days and check ins and returns you to Day 1. This can't be undone.</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setConfirmReset(false)} className="flex-1 py-2.5 rounded-full border border-subtle text-ink-soft font-semibold text-sm">
                Cancel
              </button>
              <button data-testid="confirm-reset-btn" onClick={doReset} className="flex-1 py-2.5 rounded-full bg-accent-c text-white font-semibold text-sm">
                Yes, reset
              </button>
            </div>
          </div>
        )}
        {!confirmDelete ? (
          <button
            data-testid="delete-account-btn"
            onClick={() => setConfirmDelete(true)}
            className="w-full bg-white text-red-500 font-semibold py-3 rounded-full flex items-center justify-center gap-2 card-shadow"
          >
            <Trash2 size={16} /> Delete my account
          </button>
        ) : (
          <div className="bg-white rounded-2xl p-4 card-shadow">
            <p className="text-sm text-ink-soft">This permanently deletes your account and all your data (check ins, symptoms, reflections and plans). This cannot be undone.</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2.5 rounded-full border border-subtle text-ink-soft font-semibold text-sm">Cancel</button>
              <button data-testid="confirm-delete-btn" onClick={doDelete} className="flex-1 py-2.5 rounded-full bg-red-500 text-white font-semibold text-sm">Delete forever</button>
            </div>
          </div>
        )}
        <button
          data-testid="logout-btn"
          onClick={doLogout}
          className="w-full text-ink-muted font-semibold py-3 rounded-full flex items-center justify-center gap-2"
        >
          <LogOut size={16} /> Log out
        </button>
      </div>
    </div>
  );
}
